import React, { useEffect, useState } from "react";
import adminApi from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Award, Loader2, X, ExternalLink, Ticket } from "lucide-react";

/**
 * AdminNudges — CRUD for the Growth Nudge Centre catalog (actions + badge tiers).
 * Admin can create/edit/deactivate actions of type "in_app" (data-driven) or
 * "offer" (external discount with reveal-code). Also manages badge tiers.
 */
export default function AdminNudges() {
  const [tab, setTab] = useState("actions");   // "actions" | "badges"
  return (
    <div data-testid="admin-nudges-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center"><Award size={18} /></div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8A9992]">Growth</div>
          <h1 className="text-2xl font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>Nudge Centre catalog</h1>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => setTab("actions")} className={`pill-tab ${tab === "actions" ? "pill-tab-active" : "pill-tab-inactive"}`} data-testid="admin-nudge-tab-actions">Actions</button>
        <button onClick={() => setTab("badges")}  className={`pill-tab ${tab === "badges"  ? "pill-tab-active" : "pill-tab-inactive"}`} data-testid="admin-nudge-tab-badges">Badge tiers</button>
      </div>
      {tab === "actions" ? <ActionsPanel /> : <BadgesPanel />}
    </div>
  );
}

// --- Actions catalog -------------------------------------------------------
function ActionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // action being edited (or "new")

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/nudges");
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load nudges"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ code: "", title: "", description: "", credits: 5, type: "in_app", cta_label: "Open", cta_href: "", external_url: "", discount_code: "", sort_order: 100, active: true });

  const save = async (payload) => {
    try {
      if (payload.id) {
        await adminApi.patch(`/admin/nudges/${payload.id}`, payload);
        toast.success("Action updated");
      } else {
        await adminApi.post("/admin/nudges", payload);
        toast.success("Action created");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? Existing doctor credits stay untouched.`)) return;
    try {
      await adminApi.delete(`/admin/nudges/${item.id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={startNew} className="btn-emerald text-sm" data-testid="admin-nudge-new">
          <Plus size={14} /> New action
        </button>
      </div>
      <div className="rounded-3xl bg-white border border-[#EFECE5] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EC] text-left text-[11px] uppercase tracking-widest text-[#5C7568]">
            <tr>
              <th className="px-4 py-3">Title / code</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Credits</th>
              <th className="px-4 py-3">Completions</th>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-[#EFECE5]" data-testid={`admin-nudge-row-${a.code}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#1F2A24]">{a.title}</div>
                  <div className="text-[11px] text-[#8A9992] font-mono">{a.code}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full ${a.type === "offer" ? "bg-[#FCE7D9] text-[#8A5A34]" : "bg-[#E6F0EC] text-[#0A4C36]"}`}>
                    {a.type === "offer" ? <Ticket size={11} /> : <Award size={11} />}
                    {a.type}
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold text-[#0A4C36]">+{a.credits}</td>
                <td className="px-4 py-3 text-[#47554D]">{a.completions ?? 0}</td>
                <td className="px-4 py-3 text-[#47554D]">{a.sort_order}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-1 rounded-full ${a.active ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#F3F1EC] text-[#8A9992]"}`}>
                    {a.active ? "Active" : "Off"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing(a)} className="text-[#0A4C36] hover:bg-[#E6F0EC] rounded-lg p-1.5 mr-1" title="Edit" data-testid={`admin-nudge-edit-${a.code}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => del(a)} className="text-[#B14A2A] hover:bg-[#FCE7D9] rounded-lg p-1.5" title="Delete" data-testid={`admin-nudge-del-${a.code}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-[#8A9992]">No actions yet — click <b>New action</b> to add one.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <ActionDrawer initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function ActionDrawer({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const setV = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-nudge-drawer">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>
            {f.id ? "Edit action" : "New action"}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Code (unique key)" required>
            <input value={f.code} onChange={(e) => setV("code", e.target.value.trim().toLowerCase().replace(/\s+/g, "_"))} disabled={!!f.id} className="input-clean" data-testid="admin-nudge-code" placeholder="conf_medex_2026" />
          </Field>
          <Field label="Title" required>
            <input value={f.title} onChange={(e) => setV("title", e.target.value)} className="input-clean" data-testid="admin-nudge-title" />
          </Field>
          <Field label="Description">
            <textarea value={f.description} onChange={(e) => setV("description", e.target.value)} className="input-clean min-h-[70px]" data-testid="admin-nudge-desc" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" required>
              <select value={f.type} onChange={(e) => setV("type", e.target.value)} className="input-clean" data-testid="admin-nudge-type">
                <option value="in_app">In-app (auto)</option>
                <option value="offer">Offer (external)</option>
              </select>
            </Field>
            <Field label="Credits" required>
              <input type="number" value={f.credits} onChange={(e) => setV("credits", Number(e.target.value))} className="input-clean" data-testid="admin-nudge-credits" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order">
              <input type="number" value={f.sort_order} onChange={(e) => setV("sort_order", Number(e.target.value))} className="input-clean" data-testid="admin-nudge-order" />
            </Field>
            <Field label="Active">
              <select value={f.active ? "1" : "0"} onChange={(e) => setV("active", e.target.value === "1")} className="input-clean" data-testid="admin-nudge-active">
                <option value="1">Active</option>
                <option value="0">Off</option>
              </select>
            </Field>
          </div>
          <Field label="CTA label">
            <input value={f.cta_label || ""} onChange={(e) => setV("cta_label", e.target.value)} className="input-clean" data-testid="admin-nudge-cta-label" placeholder="Reveal my code" />
          </Field>
          {f.type === "in_app" ? (
            <Field label="Internal route (CTA takes doctor here)">
              <input value={f.cta_href || ""} onChange={(e) => setV("cta_href", e.target.value)} className="input-clean" data-testid="admin-nudge-href" placeholder="/care-circle" />
            </Field>
          ) : (
            <>
              <Field label="External URL">
                <input value={f.external_url || ""} onChange={(e) => setV("external_url", e.target.value)} className="input-clean" data-testid="admin-nudge-url" placeholder="https://…" />
              </Field>
              <Field label="Discount code (shown on reveal)">
                <input value={f.discount_code || ""} onChange={(e) => setV("discount_code", e.target.value)} className="input-clean font-mono" data-testid="admin-nudge-discount" placeholder="HC-MEDEX-20" />
              </Field>
            </>
          )}
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-outline-emerald text-sm" data-testid="admin-nudge-cancel">Cancel</button>
          <button onClick={() => onSave(f)} className="btn-emerald text-sm" data-testid="admin-nudge-save">Save</button>
        </div>
      </div>
    </div>
  );
}

// --- Badge tiers -----------------------------------------------------------
function BadgesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await adminApi.get("/admin/nudge-badges");
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load badges"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", min_credits: 0, max_credits: null, icon: "🏅", sort_order: 100, active: true });
  const save = async (p) => {
    try {
      if (p.id) await adminApi.patch(`/admin/nudge-badges/${p.id}`, p);
      else      await adminApi.post("/admin/nudge-badges", p);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const del = async (b) => {
    if (!window.confirm(`Delete tier "${b.name}"?`)) return;
    try { await adminApi.delete(`/admin/nudge-badges/${b.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={startNew} className="btn-emerald text-sm" data-testid="admin-badge-new"><Plus size={14} /> New tier</button>
      </div>
      <div className="rounded-3xl bg-white border border-[#EFECE5] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EC] text-left text-[11px] uppercase tracking-widest text-[#5C7568]">
            <tr>
              <th className="px-4 py-3">Tier</th>
              <th className="px-4 py-3">Credit range</th>
              <th className="px-4 py-3">Order</th>
              <th className="px-4 py-3">Active</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-[#EFECE5]" data-testid={`admin-badge-row-${b.name}`}>
                <td className="px-4 py-3">
                  <span className="text-xl mr-2">{b.icon}</span>
                  <span className="font-semibold text-[#1F2A24]">{b.name}</span>
                </td>
                <td className="px-4 py-3 text-[#47554D]">{b.min_credits} – {b.max_credits ?? "∞"}</td>
                <td className="px-4 py-3 text-[#47554D]">{b.sort_order}</td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] px-2 py-1 rounded-full ${b.active ? "bg-[#E6F0EC] text-[#0A4C36]" : "bg-[#F3F1EC] text-[#8A9992]"}`}>
                    {b.active ? "Active" : "Off"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button onClick={() => setEditing(b)} className="text-[#0A4C36] hover:bg-[#E6F0EC] rounded-lg p-1.5 mr-1" data-testid={`admin-badge-edit-${b.name}`}><Pencil size={14} /></button>
                  <button onClick={() => del(b)} className="text-[#B14A2A] hover:bg-[#FCE7D9] rounded-lg p-1.5" data-testid={`admin-badge-del-${b.name}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-[#8A9992]">No badge tiers yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <BadgeDrawer initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function BadgeDrawer({ initial, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const setV = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full md:max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-badge-drawer">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>
            {f.id ? "Edit tier" : "New tier"}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="space-y-3">
          <Field label="Name" required>
            <input value={f.name} onChange={(e) => setV("name", e.target.value)} className="input-clean" data-testid="admin-badge-name" />
          </Field>
          <Field label="Icon (emoji)">
            <input value={f.icon} onChange={(e) => setV("icon", e.target.value)} className="input-clean" data-testid="admin-badge-icon" placeholder="🥇" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min credits" required>
              <input type="number" value={f.min_credits} onChange={(e) => setV("min_credits", Number(e.target.value))} className="input-clean" data-testid="admin-badge-min" />
            </Field>
            <Field label="Max credits (empty = ∞)">
              <input type="number" value={f.max_credits ?? ""} onChange={(e) => setV("max_credits", e.target.value === "" ? null : Number(e.target.value))} className="input-clean" data-testid="admin-badge-max" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sort order">
              <input type="number" value={f.sort_order} onChange={(e) => setV("sort_order", Number(e.target.value))} className="input-clean" data-testid="admin-badge-order" />
            </Field>
            <Field label="Active">
              <select value={f.active ? "1" : "0"} onChange={(e) => setV("active", e.target.value === "1")} className="input-clean" data-testid="admin-badge-active">
                <option value="1">Active</option>
                <option value="0">Off</option>
              </select>
            </Field>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline-emerald text-sm">Cancel</button>
          <button onClick={() => onSave(f)} className="btn-emerald text-sm" data-testid="admin-badge-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-1.5">{label}{required && <span className="text-[#B14A2A] ml-0.5">*</span>}</div>
      {children}
    </label>
  );
}

function Loading() { return <div className="py-16 flex items-center justify-center text-[#8A9992]"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>; }
